import { PersonFilter } from '~/types/api.graphql';
import { pulseTopicOptionLabel } from '~/utils/pulse';
import PersonFilterHelper from '~/views/audiences/AudiencePage/Tabs/OverviewPage/helpers/PersonFilterHelper';

import { ConfirmationTranslate, translateFieldLabel } from './confirmationCopy';
import {
  MutationConfirmation,
  MutationConfirmationFieldClassification,
  MutationConfirmationSchemaProperty,
} from './mutationConfirmation.types';

export interface CardField {
  name: string;
  label: string;
  value: unknown;
  required: boolean;
  sensitive: boolean;
  display?: string;
  schemaType?: string;
  stringArray: boolean;
}

export const buildFieldList = (confirmation: MutationConfirmation, translate?: ConfirmationTranslate): CardField[] => {
  const properties = confirmation.schema?.properties ?? {};
  const classifications = confirmation.fields ?? {};
  const values = confirmation.values ?? {};
  const required = new Set(confirmation.schema?.required ?? []);

  const names = Object.keys(properties).length
    ? Object.keys(properties)
    : [...new Set([...Object.keys(classifications), ...Object.keys(values)])];

  return names
    .filter((name) => !classifications[name]?.systemProvided)
    .filter((name) => !isUnproposedStructuredField(properties[name], values[name]))
    .map((name) => ({
      name,
      label: resolveLabel(name, confirmation.tool, classifications[name], properties[name], translate),
      value: values[name],
      required: required.has(name),
      sensitive: !!classifications[name]?.sensitive,
      display: classifications[name]?.display,
      schemaType: resolveSchemaType(properties[name]),
      stringArray: isStringArraySchema(properties[name]),
    }));
};

const resolveLabel = (
  name: string,
  tool: string,
  classification: MutationConfirmationFieldClassification | undefined,
  property: MutationConfirmationSchemaProperty | undefined,
  translate: ConfirmationTranslate | undefined,
): string => {
  const fallback = resolveFieldLabel(name, classification, property);
  return translate ? translateFieldLabel(translate, tool, name, fallback) : fallback;
};

const resolveFieldLabel = (
  name: string,
  classification: MutationConfirmationFieldClassification | undefined,
  property: MutationConfirmationSchemaProperty | undefined,
): string => {
  const label = classification?.label?.trim();
  if (label) return label;
  const title = typeof property?.title === 'string' ? property.title.trim() : '';
  if (title) return title;
  return humanizeFieldName(name) || name;
};

const humanizeFieldName = (name: string): string =>
  name
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export const resolveSchemaType = (property: MutationConfirmationSchemaProperty | undefined): string | undefined => {
  const type = resolveSchemaBranch(property)?.type;
  if (typeof type === 'string') return type;
  return Array.isArray(type) ? type.find((entry) => entry !== 'null') : undefined;
};

export const isStringArraySchema = (property: MutationConfirmationSchemaProperty | undefined): boolean => {
  const branch = resolveSchemaBranch(property);
  if (resolveSchemaType(branch) !== 'array') return false;
  const items = branch?.items;
  return items === undefined || (isSchemaProperty(items) && resolveSchemaType(items) === 'string');
};

const resolveSchemaBranch = (
  property: MutationConfirmationSchemaProperty | undefined,
): MutationConfirmationSchemaProperty | undefined => {
  if (!property) return undefined;
  if (property.type !== undefined) return property;
  const branches: MutationConfirmationSchemaProperty[] = property.anyOf ?? property.oneOf ?? [];
  return branches.map((branch) => resolveSchemaBranch(branch)).find((branch) => branch && branch.type !== 'null');
};

const isUnproposedStructuredField = (
  property: MutationConfirmationSchemaProperty | undefined,
  value: unknown,
): boolean => {
  if (value !== null && value !== undefined) return false;
  if (isStringArraySchema(property)) return false;
  const type = resolveSchemaType(property);
  return type === 'array' || type === 'object';
};

const isSchemaProperty = (value: unknown): value is MutationConfirmationSchemaProperty =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export const isBooleanField = (field: CardField): boolean =>
  field.schemaType === 'boolean' || typeof field.value === 'boolean';

export const formatDisplayFieldValue = (field: CardField): string | null =>
  DISPLAY_VALUE_FORMATTERS[field.display ?? '']?.(field.value) ??
  DISPLAY_VALUE_FORMATTERS[field.name]?.(field.value) ??
  null;

export const formatFiltersValue = (value: unknown): string =>
  (Array.isArray(value) ? value : [])
    .map((entry) => {
      try {
        return PersonFilterHelper.createInstance(entry as PersonFilter)?.getTooltip();
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('confirmationFields: failed to format a geo/person filter', entry, error);
        return undefined;
      }
    })
    .filter((text): text is string => !!text)
    .join('\n');

const DISPLAY_VALUE_FORMATTERS: Record<string, (value: unknown) => string> = {
  pulseTopics: (value) =>
    (Array.isArray(value) ? value.filter((entry): entry is string => typeof entry === 'string') : [])
      .map(pulseTopicOptionLabel)
      .join('\n'),
  filters: formatFiltersValue,
};

export const isStructuredField = (field: CardField): boolean =>
  field.value !== null && field.value !== undefined && typeof field.value === 'object';

export const coerceFieldValue = (raw: unknown, field: CardField): unknown => {
  if (typeof raw === 'boolean') return raw;
  if (typeof raw !== 'string') return raw;
  if (raw === '') return field.required ? '' : null;
  if (isNumericField(field)) {
    return isNumericValueValid(raw, field) ? Number(raw) : raw;
  }
  return raw;
};

export const isNumericField = (field: CardField): boolean =>
  field.schemaType === 'integer' || field.schemaType === 'number';

export const isNumericValueValid = (raw: string, field: CardField): boolean => {
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) return false;
  return field.schemaType !== 'integer' || Number.isInteger(parsed);
};
